import { set } from '@ember/object';
import Route from '@ember/routing/route';
import { type Registry as Services, service } from '@ember/service';
import type { Period } from 'lumen/models';
import type { Transition } from 'lumen/services/transition';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';

type RouteQueryParams = {
  maxPersons: number;
};

export interface HotelParityPosModel {
  isLoading: boolean;
  periods: Period[];
}

export default class HotelParityPosRoute extends Route {
  queryParams = {
    maxPersons: {
      refreshModel: true,
    },
  };

  @service declare access: Services['access'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare liveupdate: Services['liveupdate'];
  @service declare store: Services['store'];
  @service declare transition: Services['transition'];

  activate(transition: Transition): void {
    super.activate(transition);
    set(this.liveupdate, 'parityPOSTabOpen', true);
  }

  beforeModel(transition: Transition): void {
    if (!this.access.hasFeature('subscription.parity.pos')) {
      this.transition.redirectToTab(transition, 'hotel.parity.list');
    } else {
      this.transition.validatePOSQueryParams(transition);
    }
  }

  deactivate(transition: Transition): void {
    super.deactivate(transition);
    set(this.liveupdate, 'parityPOSTabOpen', false);
  }

  model(params: RouteQueryParams): HotelParityPosModel {
    const model: HotelParityPosModel = {
      periods: [],
      isLoading: true,
    };

    const today = this.hotelAndMarketService.getLocalTime();
    const dateRangeStart = today.format('YYYY-MM-DD');
    const dateRangeEnd = today.add(29, 'day').format('YYYY-MM-DD');

    const requestParityPos = {
      namespace: APIGATEWAY,
      typeKey: 'parities/pos',
      hotel_id: this.hotelAndMarketService.hotel?.id,
      from_date_range_start: dateRangeStart,
      from_date_range_end: dateRangeEnd,
      los: this.hotelAndMarketService.hotel?.defaultLosParity[0],
      persons: params.maxPersons,
      membershiptype: 0,
      meta: 'nested',
    };
    void this.store
      .query<Period>('period', requestParityPos)
      .then((requestedPosParities) => {
        set(model, 'periods', [...model.periods, ...requestedPosParities]);
        set(model, 'isLoading', false);
      })
      .catch((e) => {
        set(model, 'isLoading', false);
        this.errorTracking.captureException(e);
      });

    this.liveupdate.updateSelection({
      currentHotelId: this.hotelAndMarketService.hotel?.id, // current hotel id
      maxPersons: params.maxPersons,
    });

    return model;
  }
}
